<template>
  <div v-if="!isExpired" class="mc-delivery-track">
    <SideBar
      v-if="!selectedMarker && !sidebarHistoryMobile"
      :loading="isLoading"
      :status="status"
      :delivery="delivery"
      :vehicle="vehicle"
      :doorActivityData="doorActivityData"
      :currentDateTime="currentDateTime"
      :detailTripHistories="detailTripHistories"
      :isMultiChamber="isMultiChamber"
      :hasTmsColdChainAccess="hasTmsColdChainAccess"
      :hasFeatureEditActivityTrackDOMGM="hasFeatureEditActivityTrackDOMGM"
      :canEditData="canEditData"
      @save-pickup="handleSavePickupEdit"
      @save-drop="handleSaveDropEdit"
      @edit-location-time-state-change="handleEditLocationTimeStateChange"
    />
    <SideBarMobile
      v-else-if="selectedMarker"
      :loading="isLoading"
      :currentDateTime="currentDateTime"
      :selectedMarker="selectedMarker"
      :hasTmsColdChainAccess="hasTmsColdChainAccess"
      @close-section="handleCloseSection"
    />
    <SideBarHistoryMobile
      v-else-if="sidebarHistoryMobile"
      :loading="isLoading"
      :status="status"
      :delivery="delivery"
      :doorActivityData="doorActivityData"
      :currentDateTime="currentDateTime"
      :detailTripHistories="detailTripHistories"
      :isMultiChamber="isMultiChamber"
      :isMultiDoor="isMultiDoor"
      :uuid="uuid"
      :hasTmsColdChainAccess="hasTmsColdChainAccess"
      :hasFeatureEditActivityTrackDOMGM="hasFeatureEditActivityTrackDOMGM"
      :isEditingLocationTime="isEditingLocationTime"
      @history-date-range-change="handleHistoryDateRangeChange"
    />
    <MapView
      :loading="isLoading"
      :status="status"
      :delivery="delivery"
      :vehicle="vehicle"
      :detailTripHistories="detailTripHistories"
      :currentDateTime="currentDateTime"
      :hasTmsColdChainAccess="hasTmsColdChainAccess"
      :isMultiDoor="isMultiDoor"
      :isMultiChamber="isMultiChamber"
      :uuid="uuid"
      :hasFeatureEditActivityTrackDOMGM="hasFeatureEditActivityTrackDOMGM"
      :isEditingLocationTime="isEditingLocationTime"
      @marker-selected="handleMarkerSelected"
      @isLiveview="handleIsLiveview"
      @history-date-range-change="handleHistoryDateRangeChange"
    />
  </div>
  <div v-else>
    <ExpirationTrack />
  </div>
</template>

<script>
import appConfig from '@/app.config'
import { Helper } from '@/mixin/helper'
import { toUTC } from '@/helpers/tzDate'
import { mapGetters } from 'vuex'
import moment from 'moment'

import SideBar from './components/SideBar'
import SideBarMobile from './components/SideBarMobile'
import SideBarHistoryMobile from './components/SideBarHistoryMobile'
import MapView from './components/MapView'
import ExpirationTrack from './components/ExpirationTrack'

import * as Delivery from '@/services/rest-api/lib/delivery/delivery-order'
import * as FleetTask from '@/services/rest-api/lib/fleet-task/activity'

import { GET_VEHICLE_STATUS_LIVETRACK, GET_DETAIL_TRIP_HISTORIES } from '@/services/api/fleet'

export default {
  page: {
    title: 'Pelacakan DO',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    SideBar,
    SideBarMobile,
    SideBarHistoryMobile,
    MapView,
    ExpirationTrack,
  },
  mixins: [Helper],
  data() {
    return {
      data: this.$route.params.data || null,
      isLoading: true,
      delivery: null,
      vehicle: null,
      vehicleId: null,
      detailTripHistories: null,
      currentDateTime: null,
      refreshInterval: null,
      uuid: null,
      token: null,
      selectedMarker: null,
      doorActivityData: null,
      isExpired: false,
      sidebarHistoryMobile: false,
      historyDateRangeFilter: null,
      canEditData: false,
      isEditingLocationTime: false,
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    status() {
      return this.delivery?.status?.name
    },
    hasTmsColdChainAccess() {
      return this.delivery?.is_cold_chain
    },
    isMultiChamber() {
      return this.vehicle?.sensorTemperature?.length > 1
    },
    isMultiDoor() {
      return this.vehicle?.sensorDoor?.length > 1
    },
    hasFeatureEditActivityTrackDOMGM() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data?.features?.edit_activity_track_do
      )
    }
  },
  mounted() {
    this.currentDateTime = this.getCurrentDateTime()
    this.getData()
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    toUTC,
    isDateChanged(newVal, oldVal) {
      if (!newVal && !oldVal) return false
      if (!newVal || !oldVal) return true

      return moment(newVal).valueOf() !== moment(oldVal).valueOf()
    },
    getCurrentDateTime() {
      return new Date().toISOString()
    },
    getData() {
      const decodedData = atob(this.data)
      const [uidPart, tokenPart] = decodedData.split('?token=')
      const newUid = btoa(uidPart) + '&&' + this.getCompanyId()
      this.uuid = btoa(newUid)
      this.token = tokenPart
      this.getDelivery()
    },
    async getDelivery() {
      const res = await Delivery.track(this.uuid)

      const isTrackingExpired = res?.errors_v2?.issues?.some(
        (item) => item.code === 'ORDER.DELIVERY_ORDER.TRACKING.EXPIRED'
      )

      if (res.status === 404) {
        const data = {
          hasButton: false,
        }
        localStorage.setItem('404', JSON.stringify(data))
        this.$router.push({
          name: '404',
        })
        return
      }

      if (res.status === 410 && isTrackingExpired) {
        this.isExpired = true
        return
      }

      if (!res.data) return

      this.delivery = res.data

      let targetFleetTask = null
      let shouldFetchTripHistories = false

      if (this.status !== 'Dijalankan') {
        if (this.delivery?.shipment_type === 'TRANSIT') {
          if (this.status === 'Diambil') {
            targetFleetTask = this.delivery.transits.find(
              (item) => item.status === 'UNCOMPLETED'
            )?.fleet_task
          } else {
            targetFleetTask = [...this.delivery.transits]
              .reverse()
              .find((item) => item.status === 'COMPLETED')?.fleet_task
          }
          shouldFetchTripHistories = true
        } else {
          targetFleetTask = this.delivery?.fleet_task
        }

        const vehicleId = targetFleetTask?.vehicle?.id
        if (vehicleId) {
          this.vehicleId = vehicleId
          this.getVehicle(vehicleId)
        }
      }

      if (
        (this.status === 'Dijalankan' || this.status === 'Dijadwalkan') &&
        this.delivery?.shipment_type === 'TRANSIT'
      ) {
        const hasCompletedTransit = this.delivery.transits?.some((t) => t.status === 'COMPLETED')
        if (hasCompletedTransit) {
          shouldFetchTripHistories = true
        }
      }

      if (shouldFetchTripHistories) {
        await this.getDetailTripHistoriesForAllVehicles()
      }

      if (!['Terkirim'].includes(this.status)) {
        if (!this.refreshInterval) {
          this.refreshInterval = setInterval(() => {
            this.currentDateTime = this.getCurrentDateTime()
            this.getDelivery()
          }, 300000)
        }
      } else {
        if (this.refreshInterval) {
          clearInterval(this.refreshInterval)
          this.refreshInterval = null
        }
      }

      this.canEditData =
        this.delivery.can_edit && this.hasFeatureEditActivityTrackDOMGM && this.status === 'Terkirim'
          ? true
          : false

      this.isLoading = false
    },
    operatedByVendor() {
      return this.delivery?.vendor?.id
    },
    getApolloContext() {
      let authToken = this.token
      if (this.operatedByVendor()) {
        authToken = this.delivery?.fleet_task?.token
      }
      return {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    },
    async getVehicle(vehicleId) {
      this.vehicleId = vehicleId
      const res = await this.$apollo.query({
        query: GET_VEHICLE_STATUS_LIVETRACK,
        fetchPolicy: 'no-cache',
        context: this.getApolloContext(),
        variables: {
          filter: {
            vehicleId: parseInt(vehicleId),
          },
        },
      })

      let sensorDoor = null
      let sensorTemperature = null
      if (this.hasTmsColdChainAccess) {
        sensorDoor = res.data.vehicleStatus.vehicleStatus.vehicle.sensor.filter((item) =>
          [4, 5].includes(item.sensorType.sensorCode)
        )
        sensorTemperature = res.data.vehicleStatus.vehicleStatus.vehicle.sensor.filter((item) =>
          [11, 12, 36].includes(item.sensorType.sensorCode)
        )
      }

      this.vehicle = res.data.vehicleStatus.vehicleStatus
      if (this.hasTmsColdChainAccess) {
        this.vehicle.sensorDoor = sensorDoor
        this.vehicle.sensorTemperature = sensorTemperature
        this.fetchAllDoorActivity()
      }
      if (this.status !== 'Dijalankan' && this.delivery?.shipment_type !== 'TRANSIT') {
        this.getDetailTripHistories(vehicleId)
      }
      this.isLoading = false
    },
    hasValidHistoryDateRange(dateRange = this.historyDateRangeFilter) {
      if (!Array.isArray(dateRange) || dateRange.length !== 2) return false

      const [startDate, endDate] = dateRange
      if (!startDate || !endDate) return false

      return !isNaN(new Date(startDate).getTime()) && !isNaN(new Date(endDate).getTime())
    },
    resolveHistoryDateRange(
      defaultStartDate,
      defaultEndDate,
      dateRange = this.historyDateRangeFilter
    ) {
      if (this.hasValidHistoryDateRange(dateRange)) {
        const [startDate, endDate] = dateRange
        return { startDate, endDate }
      }

      return {
        startDate: defaultStartDate,
        endDate: defaultEndDate,
      }
    },
    async getDetailTripHistories(vehicleId, dateRange = this.historyDateRangeFilter) {
      const pickup = this.delivery?.delivery_order_activity?.pickup
      const drop = this.delivery?.delivery_order_activity?.drop
      let defaultStartDate = pickup?.geofence_in || pickup?.completed_on
      let defaultEndDate = drop?.geofence_out || drop?.completed_on || this.getCurrentDateTime()

      if (this.hasFeatureEditActivityTrackDOMGM) {
        if (defaultStartDate) {
          const threeDaysAfterStart = new Date(defaultStartDate)
          threeDaysAfterStart.setDate(threeDaysAfterStart.getDate() + 3)
          const currentEndDate = new Date(defaultEndDate)
          if (currentEndDate > threeDaysAfterStart) {
            defaultEndDate = threeDaysAfterStart.toISOString()
          }
        }
      }

      const { startDate, endDate } = this.resolveHistoryDateRange(
        defaultStartDate,
        defaultEndDate,
        dateRange
      )

      const res = await this.$apollo.query({
        query: GET_DETAIL_TRIP_HISTORIES,
        fetchPolicy: 'no-cache',
        context: this.getApolloContext(),
        variables: {
          filter: {
            vehicleId: parseInt(vehicleId),
            startDate: toUTC(startDate),
            endDate: toUTC(endDate),
            column: ['door1', 'door2'],
          },
        },
      })
      this.detailTripHistories = res.data.detailTripHistories.detailTripHistories
      this.isLoading = false
    },
    async getDetailTripHistoriesForAllVehicles(dateRange = this.historyDateRangeFilter) {
      const trips = []
      const hasSelectedRange = this.hasValidHistoryDateRange(dateRange)

      for (const transit of this.delivery.transits) {
        const vehicleId = transit.fleet_task?.vehicle?.id
        const pickup = transit.delivery_order_activity?.pickup
        const drop = transit.delivery_order_activity?.drop
        let startDate = pickup?.geofence_in || pickup?.completed_on
        let endDate = drop?.geofence_out || drop?.completed_on || this.getCurrentDateTime()
        const authToken = transit.fleet_task?.token

        if (this.hasFeatureEditActivityTrackDOMGM) {
          if (startDate) {
            const threeDaysAfterStart = new Date(startDate)
            threeDaysAfterStart.setDate(threeDaysAfterStart.getDate() + 3)
            const currentEndDate = new Date(endDate)
            if (currentEndDate > threeDaysAfterStart) {
              endDate = threeDaysAfterStart.toISOString()
            }
          }
        }

        const { startDate: resolvedStartDate, endDate: resolvedEndDate } =
          this.resolveHistoryDateRange(startDate, endDate, dateRange)

        if (!vehicleId || (!resolvedStartDate && !hasSelectedRange)) continue

        const res = await this.$apollo.query({
          query: GET_DETAIL_TRIP_HISTORIES,
          fetchPolicy: 'no-cache',
          context: {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          },
          variables: {
            filter: {
              vehicleId: parseInt(vehicleId),
              startDate: toUTC(resolvedStartDate),
              endDate: toUTC(resolvedEndDate),
            },
          },
        })

        trips.push({
          vehicleId,
          tripHistories: res.data.detailTripHistories.detailTripHistories,
        })
      }

      this.detailTripHistories = this.mergeTripHistories(trips)
      this.isLoading = false
    },
    mergeTripHistories(trips) {
      const merged = {
        data: [],
        stoppage: [],
      }

      for (const trip of trips) {
        if (trip.tripHistories?.data) {
          merged.data.push(...trip.tripHistories.data)
          merged.data.push({ break: true })
        }
        if (trip.tripHistories?.stoppage) {
          merged.stoppage.push(...trip.tripHistories.stoppage)
        }
      }

      return merged
    },
    handleMarkerSelected(marker) {
      this.selectedMarker = marker
    },
    handleCloseSection() {
      this.selectedMarker = null
    },
    async fetchAllDoorActivity() {
      const availableDoorSensorCodes = (this.vehicle?.sensorDoor || [])
        .map((sensor) => sensor?.sensorType?.sensorCode)
        .filter((code) => [4, 5].includes(code))

      const orderedDoorSensorCodes = [4, 5].filter((code) =>
        availableDoorSensorCodes.includes(code)
      )

      const res = await Promise.all(
        orderedDoorSensorCodes.map(async (sensorCode) => {
          const data = await this.fetchDoorActivity(sensorCode)
          return { sensorCode, data }
        })
      )

      this.doorActivityData = res
    },
    async fetchDoorActivity(sensorCode) {
      const res = await Delivery.getDoorActivity(this.uuid, sensorCode)
      return res.data
    },
    handleIsLiveview(isLiveview) {
      if (isLiveview === 'history' && window.innerWidth < 890) {
        this.sidebarHistoryMobile = true
      } else {
        this.sidebarHistoryMobile = false
      }
      this.selectedMarker = false
    },
    handleEditLocationTimeStateChange(isEditing) {
      this.isEditingLocationTime = Boolean(isEditing)
    },
    async handleHistoryDateRangeChange(dateRange) {
      this.historyDateRangeFilter = this.hasValidHistoryDateRange(dateRange) ? dateRange : null

      if (this.delivery?.shipment_type === 'TRANSIT') {
        await this.getDetailTripHistoriesForAllVehicles(this.historyDateRangeFilter)
        return
      }

      const vehicleId = this.vehicleId || this.delivery?.fleet_task?.vehicle?.id
      if (!vehicleId) return

      await this.getDetailTripHistories(vehicleId, this.historyDateRangeFilter)
    },
    async handleSavePickupEdit(payload) {
      if (!this.delivery) return

      this.isLoading = true

      let fleetTaskId = this.delivery.fleet_task?.id
      let pickupActivityId = this.delivery.delivery_order_activity?.pickup?.id
      let currentActivity = this.delivery.delivery_order_activity?.pickup

      if (this.delivery?.shipment_type === 'TRANSIT') {
        const originId = this.delivery?.origin_address?.id
        const transit = this.delivery?.transits?.find((t) => t.origin_address?.id === originId)
        if (transit) {
          fleetTaskId = transit.fleet_task?.id
          pickupActivityId = transit.delivery_order_activity?.pickup?.id
          currentActivity = transit.delivery_order_activity?.pickup
        }
      }

      if (!fleetTaskId || !pickupActivityId) {
        this.$mcToast.error('Data penugasan atau aktivitas tidak ditemukan')
        this.isLoading = false
        return
      }

      const updateData = {}
      if (
        payload?.geofence_in &&
        this.isDateChanged(payload?.geofence_in, currentActivity?.geofence_in)
      ) {
        updateData.geofence_in = this.formatToMinute(payload?.geofence_in)
      }
      if (
        payload?.completed_on &&
        this.isDateChanged(payload?.completed_on, currentActivity?.completed_on)
      ) {
        updateData.completed_on = this.formatToMinute(payload?.completed_on)
      }
      if (
        payload?.geofence_out &&
        this.isDateChanged(payload?.geofence_out, currentActivity?.geofence_out)
      ) {
        updateData.geofence_out = this.formatToMinute(payload?.geofence_out)
      }

      if (Object.keys(updateData).length === 0) {
        this.isLoading = false
        return
      }

      const res = await FleetTask.updateActivity(fleetTaskId, pickupActivityId, updateData)

      if (res && res.errors_v2) {
        this.$mcToast.error('Perubahan data gagal disimpan')
        this.isLoading = false
        return
      }

      this.$mcToast.success('Perubahan data berhasil disimpan')
      await this.getDelivery()
      this.isLoading = false
    },
    async handleSaveDropEdit(payload) {
      if (!this.delivery) return

      this.isLoading = true

      let fleetTaskId = this.delivery.fleet_task?.id
      let dropActivityId = this.delivery.delivery_order_activity?.drop?.id
      let currentActivity = this.delivery.delivery_order_activity?.drop

      if (this.delivery?.shipment_type === 'TRANSIT') {
        const destinationId = this.delivery?.destination_address?.id
        const transit = this.delivery?.transits?.find(
          (t) => t.destination_address?.id === destinationId
        )
        if (transit) {
          fleetTaskId = transit.fleet_task?.id
          dropActivityId = transit.delivery_order_activity?.drop?.id
          currentActivity = transit.delivery_order_activity?.drop
        }
      }

      if (!fleetTaskId || !dropActivityId) {
        this.$mcToast.error('Data penugasan atau aktivitas tidak ditemukan')
        this.isLoading = false
        return
      }

      const updateData = {}
      if (
        payload?.geofence_in &&
        this.isDateChanged(payload?.geofence_in, currentActivity?.geofence_in)
      ) {
        updateData.geofence_in = this.formatToMinute(payload?.geofence_in)
      }
      if (
        payload?.completed_on &&
        this.isDateChanged(payload?.completed_on, currentActivity?.completed_on)
      ) {
        updateData.completed_on = this.formatToMinute(payload?.completed_on)
      }
      if (
        payload?.geofence_out &&
        this.isDateChanged(payload?.geofence_out, currentActivity?.geofence_out)
      ) {
        updateData.geofence_out = this.formatToMinute(payload?.geofence_out)
      }

      if (Object.keys(updateData).length === 0) {
        this.isLoading = false
        return
      }

      const res = await FleetTask.updateActivity(fleetTaskId, dropActivityId, updateData)

      if (res && res.errors_v2) {
        this.$mcToast.error('Perubahan data gagal disimpan')
        this.isLoading = false
        return
      }

      this.$mcToast.success('Perubahan data berhasil disimpan')
      await this.getDelivery()
      this.isLoading = false
    },
  },
}
</script>
